import React from 'react';
import { PrimaryCta, ShimmerBox } from '../../../components';
import Utils from '../../../common/utils';
const REELAX_FALLBACK = "/assets/images/Reelax.png";

/**
 * AnalyticsHeader - Header section for analytics page
 */
const AnalyticsHeader = ({
    campaignDetail,
    brandData,
    orgData,
    userData,
    campaignReport,
    campaignAnalytic,
    formattedTime,
    reportType,
    onReportTypeToggle,
    onShareClick,
    onRegenerateClick,
    onDownloadClick,
    isReportInProgress,
    pdfLoading,
    getReportTotalSpent,
    influencerCount,
    contentCount,
    campaignPlatform,
    isLoading = false, // New prop for loading state
    isReportGenerated = true, // New prop for report generated state

    // New Props
    onGenerateReport,
    refreshDisabled,
    refreshTimeLeft,
    lastGeneratedDate
}) => {
    // Derive platform label from available campaign data
    const platformLabel = (() => {
        try {
            let contentTypes = campaignDetail?.campaign_data?.campaign_data?.content_types || campaignDetail?.campaign_data?.content_types;

            // If it's a string (old data), parse it
            if (typeof contentTypes === 'string') {
                try {
                    contentTypes = JSON.parse(contentTypes);
                } catch (e) {
                    // ignore parse error
                }
            }

            if (Array.isArray(contentTypes) && contentTypes.length > 0) {
                if (contentTypes[0].platform_name) return contentTypes[0].platform_name;
                if (contentTypes[0].content_type_id) {
                    return Utils.socialMediaPlatform(contentTypes[0].content_type_id) || contentTypes[0].platform_id || null;
                }
            }
        } catch (e) {
            console.error("Error parsing content types:", e);
        }

        const deep = campaignDetail?.campaign_data?.content_type_deep_data?.[0];
        const contentTypeId = deep?.content_data?.content_type_id || campaignDetail?.campaign_data?.content_type_id;
        if (contentTypeId) {
            const label = Utils.socialMediaPlatform(contentTypeId);
            if (label) return label;
        }

        return null;
    })();

    // Shimmer loading state for header
    if (isLoading) {
        return (
            <div className="analytics-header">
                <div className="campaign-info-card">
                    <div className="campaign-info-header">
                        <div className="campaign-info-left">
                            <div className="campaign-logo" style={{ overflow: 'hidden' }}>
                                <ShimmerBox width="100%" height="100%" borderRadius="8px" />
                            </div>
                            <ShimmerBox width="200px" height="28px" />
                        </div>
                    </div>

                    <div className="campaign-stats-row">
                        <div className="stat-item">
                            <ShimmerBox width="60px" height="12px" margin="0 0 8px 0" />
                            <ShimmerBox width="80px" height="20px" />
                        </div>
                        <div className="stat-item">
                            <ShimmerBox width="60px" height="12px" margin="0 0 8px 0" />
                            <ShimmerBox width="100px" height="20px" />
                        </div>
                        <div className="stat-item">
                            <ShimmerBox width="70px" height="12px" margin="0 0 8px 0" />
                            <ShimmerBox width="90px" height="20px" />
                        </div>
                        <div className="stat-item">
                            <ShimmerBox width="70px" height="12px" margin="0 0 8px 0" />
                            <ShimmerBox width="50px" height="20px" />
                        </div>
                        <div className="stat-item">
                            <ShimmerBox width="60px" height="12px" margin="0 0 8px 0" />
                            <ShimmerBox width="50px" height="20px" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="analytics-header">
            <div className="campaign-info-card">
                <div className="campaign-info-header">
                    <div className="campaign-info-left">
                        <div className="campaign-logo" style={{ overflow: 'hidden' }}>
                            <img
                                src={brandData?.image_id
                                    ? Utils.generateBrandLogoUrl(brandData?.org_id, brandData?.image_id)
                                    : REELAX_FALLBACK
                                }
                                alt={brandData?.name || "Brand"}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.src = REELAX_FALLBACK; }}
                            />
                        </div>

                        <h2 className='typographyMedium'>{campaignDetail?.campaign_data?.name || "Campaign Name"}</h2>
                        {/* Platform shown in stats row below */}
                    </div>


                </div>

                {!isReportGenerated ? (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        borderRadius: '12px',
                        minHeight: '100px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px dashed #e5e7eb',
                        margin: '20px 0',
                        backgroundColor: '#fafafa'
                    }}>
                        <h3 style={{ fontSize: '16px', color: '#6B7280', fontWeight: 600, margin: 0 }}>Report not generated yet</h3>
                        <p style={{ color: '#9CA3AF', marginTop: '8px', fontSize: '14px', margin: '8px 0 0 0' }}>Click 'Generate Report' to view campaign stats.</p>
                    </div>
                ) : (
                    <div className="campaign-stats-row">
                        <div className="stat-item">
                            <label className='caption_2_10'>Platform</label>
                            <div className="typographyMedium">{campaignPlatform || platformLabel || "-"}</div>
                        </div>
                        <div className="stat-item">
                            <label className='caption_2_10'>Payout</label>
                            <div className="typographyMedium">
                                {(() => {
                                    const completedSpent = Number(reportType === 'client'
                                        ? campaignReport?.total_spent_client
                                        : campaignReport?.total_spent) || 0;
                                    const completedProduct = Number(campaignReport?.total_product_price) || 0;
                                    const payout = completedSpent + completedProduct;
                                    return payout > 0 ? Utils.convertFromINR(payout, userData?.country) : "-";
                                })()}
                            </div>
                        </div>
                        <div className="stat-item">
                            <label className='caption_2_10'>Total Spent</label>
                            <div className="typographyMedium">
                                {(() => {
                                    const baseSpent = typeof getReportTotalSpent === "function"
                                        ? (Number(getReportTotalSpent()) || 0)
                                        : (Number(reportType === "client"
                                            ? campaignReport?.total_spent_client
                                            : campaignReport?.total_spent) || 0);
                                    const totalProduct = Number(campaignReport?.total_product_price) || 0;
                                    const total = baseSpent + totalProduct;
                                    return total > 0 ? Utils.convertFromINR(total, userData?.country) : "-";
                                })()}
                            </div>
                        </div>
                        <div className="stat-item">
                            <label className='caption_2_10'>Influencers</label>
                            <div className="typographyMedium">
                                {(influencerCount && influencerCount > 0) ? influencerCount : "-"}
                            </div>
                        </div>
                        <div className="stat-item">
                            <label className='caption_2_10'>Posts Live</label>
                            <div className="typographyMedium">
                                {(contentCount && contentCount > 0) ? contentCount : "-"}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalyticsHeader;
